// Vertrouwenspersoon-module — Spec 35
//
// Casus-registratie, acties, vervolgstappen en dashboard-blok voor VPs.
// Privacy by design: betrokkeneOmschrijving is vrije tekst (géén FK naar Employee).

import { z } from 'zod'

// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

export const VERTROUWENS_CASUS_CATEGORIEEN = [
  'ONGEWENSTE_OMGANGSVORMEN',
  'INTEGRITEIT',
  'ARBEIDSCONFLICT',
  'PRIVE',
  'OVERIG',
] as const
export type VertrouwensCasusCategorie = (typeof VERTROUWENS_CASUS_CATEGORIEEN)[number]

export const VERTROUWENS_CASUS_CATEGORIE_LABELS: Record<VertrouwensCasusCategorie, string> = {
  ONGEWENSTE_OMGANGSVORMEN: 'Ongewenste omgangsvormen',
  INTEGRITEIT:              'Integriteit',
  ARBEIDSCONFLICT:          'Arbeidsconflict',
  PRIVE:                    'Prive',
  OVERIG:                   'Overig',
}

export const VERTROUWENS_CASUS_STATUSSEN = [
  'OPEN',
  'LOPEND',
  'AFGESLOTEN',
] as const
export type VertrouwensCasusStatus = (typeof VERTROUWENS_CASUS_STATUSSEN)[number]

export const VERTROUWENS_CASUS_STATUS_LABELS: Record<VertrouwensCasusStatus, string> = {
  OPEN:       'Open',
  LOPEND:     'Lopend',
  AFGESLOTEN: 'Afgesloten',
}

export const VERTROUWENS_CASUS_STATUS_BADGE_VARIANTS: Record<
  VertrouwensCasusStatus,
  'warning' | 'info' | 'success'
> = {
  OPEN:       'warning',
  LOPEND:     'info',
  AFGESLOTEN: 'success',
}

// ─── Zod-schemas ─────────────────────────────────────────────────────────────

export const createVertrouwensCasusSchema = z.object({
  categorie:              z.enum(VERTROUWENS_CASUS_CATEGORIEEN),
  onderwerp:              z.string().min(1).max(200),
  omschrijving:           z.string().nullable().optional(),
  betrokkeneOmschrijving: z.string().max(500).nullable().optional(),
  // NOOIT .cuid() op ID-refs — legacy UUID-format IDs bestaan in productie.
  contactRequestId:       z.string().min(1).nullable().optional(),
})
export type CreateVertrouwensCasusInput = z.infer<typeof createVertrouwensCasusSchema>

export const updateVertrouwensCasusSchema = z.object({
  categorie:              z.enum(VERTROUWENS_CASUS_CATEGORIEEN).optional(),
  onderwerp:              z.string().min(1).max(200).optional(),
  omschrijving:           z.string().nullable().optional(),
  betrokkeneOmschrijving: z.string().max(500).nullable().optional(),
  contactRequestId:       z.string().min(1).nullable().optional(),
  status:                 z.enum(VERTROUWENS_CASUS_STATUSSEN).optional(),
})
export type UpdateVertrouwensCasusInput = z.infer<typeof updateVertrouwensCasusSchema>

export const createVertrouwensCasusActieSchema = z.object({
  // ISO yyyy-MM-dd datumstring; kan in het verleden liggen.
  datum:        z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datum-formaat (YYYY-MM-DD)'),
  omschrijving: z.string().min(1),
})
export type CreateVertrouwensCasusActieInput = z.infer<typeof createVertrouwensCasusActieSchema>

export const updateVertrouwensCasusActieSchema = z.object({
  datum:        z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datum-formaat (YYYY-MM-DD)').optional(),
  omschrijving: z.string().min(1).optional(),
})
export type UpdateVertrouwensCasusActieInput = z.infer<typeof updateVertrouwensCasusActieSchema>

export const createVertrouwensCasusVervolgstapSchema = z.object({
  omschrijving: z.string().min(1).max(300),
  // Optionele datum-reminder; ISO yyyy-MM-dd of null.
  herinnerOp:   z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datum-formaat (YYYY-MM-DD)').nullable().optional(),
})
export type CreateVertrouwensCasusVervolgstapInput = z.infer<typeof createVertrouwensCasusVervolgstapSchema>

export const updateVertrouwensCasusVervolgstapSchema = z.object({
  omschrijving: z.string().min(1).max(300).optional(),
  herinnerOp:   z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datum-formaat (YYYY-MM-DD)').nullable().optional(),
  // `afgehandeld: true` zet afgehandeldOp; `false` heropent (wist afgehandeldOp).
  afgehandeld:  z.boolean().optional(),
})
export type UpdateVertrouwensCasusVervolgstapInput = z.infer<typeof updateVertrouwensCasusVervolgstapSchema>

// ─── DTOs voor API-responses ──────────────────────────────────────────────────

export interface VertrouwensCasusActieDTO {
  id:           string
  casusId:      string
  datum:        string   // ISO yyyy-MM-dd
  omschrijving: string
  createdAt:    string
  updatedAt:    string
}

export interface VertrouwensCasusVervolgstapDTO {
  id:                      string
  casusId:                 string
  omschrijving:            string
  herinnerOp:              string | null  // ISO yyyy-MM-dd
  afgehandeldOp:           string | null  // ISO datetime
  notificatieVerzondenOp:  string | null
  createdAt:               string
  updatedAt:               string
}

export interface VertrouwensCasusDetailDTO {
  id:                     string
  casusNummer:            string
  eigenaarId:             string
  eigenaarNaam:           string | null
  categorie:              VertrouwensCasusCategorie
  status:                 VertrouwensCasusStatus
  onderwerp:              string
  omschrijving:           string | null
  betrokkeneOmschrijving: string | null
  contactRequestId:       string | null
  afgeslotenOp:           string | null
  acties:                 VertrouwensCasusActieDTO[]
  vervolgstappen:         VertrouwensCasusVervolgstapDTO[]
  createdAt:              string
  updatedAt:              string
}

export interface VertrouwensCasusListItemDTO {
  id:                  string
  casusNummer:         string
  onderwerp:           string
  categorie:           VertrouwensCasusCategorie
  status:              VertrouwensCasusStatus
  eigenaarId:          string
  eigenaarNaam:        string | null
  // ISO datetime van de meest recente actie, of null als er geen zijn.
  laatsteActieOp:      string | null
  openVervolgstappen:  number
  createdAt:           string
}

// Gebruikt voor het VP-dashboard-blok: openstaande vervolgstappen inclusief
// casus-context voor weergave zonder extra fetch.
export interface OpenVervolgstapDTO {
  id:                      string
  casusId:                 string
  casusNummer:             string
  casusOnderwerp:          string
  omschrijving:            string
  herinnerOp:              string | null
  afgehandeldOp:           string | null
  notificatieVerzondenOp:  string | null
  createdAt:               string
  updatedAt:               string
}
